import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { MessageService } from 'primeng/api';
import { Popover, PopoverModule } from 'primeng/popover';
import { SelectButtonModule } from 'primeng/selectbutton';

import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';

import { firstValueFrom } from 'rxjs';
import { Settings, SETTINGS } from '../../../../app.settings';
import { notifyAndTrackError } from '../../../../common/error';
import { getItemDescriptionColumn, isStringEmpty, isStringEqual } from '../../../../common/functions';
import { AppInitService } from '../../../../services/app-init.service';
import { AppInsightsService } from '../../../../services/app-insights.service';
import { IAppUser, InsectaryDashboardService, ISearchFilterValue, ISearchRequest, ISearchRequestFilter } from '../../../../services/insecta';
import { DialogModule } from 'primeng/dialog';

@Component({
  selector: 'app-insectary-calendar',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectButtonModule, PopoverModule, FullCalendarModule, DialogModule],
  templateUrl: './insectary-calendar.component.html'
})
export class InsectaryCalendarComponent implements OnInit, OnDestroy {
  private settings: Settings = inject(SETTINGS);
  private readonly msgService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly appInit = inject(AppInitService);
  private readonly appInsights = inject(AppInsightsService);

  private searchSubject$ = new Subject<void>();
  private destroy$ = new Subject<void>();
  private lastRequestJson: string = '';

  @ViewChild('calPanel') calPanel!: HTMLElement;
  @ViewChild('calendar') calendar!: FullCalendarComponent;
  @ViewChild('op') op!: Popover;
  @ViewChild('calendarContainer', { static: false }) calendarContainer!: ElementRef;

  @Input() siteName: string = '';
  @Input() showAll: boolean = false;
  @Input() undeliverableDates: any[] = [];
  @Input() undeliverableDays: any[] = [];

  @Output() calendarReady = new EventEmitter<void>();

  appUser!: IAppUser;
  userSettingsJson: { [key: string]: any } = {};

  popupRequest: any;
  events: any[] = [];

  isInitialLoad: boolean = false;
  greaterthanDate: string = '';
  lessThandate: string = '';

  displayMoreDialog = false;
  selectedDateEvents: any[] = [];
  private hoverTimeout: any;
  dialogMaxHeight = '0px';
  dialogStyle: any = {};

  pageVars: any = {
    currentUser: ``,
    isEditing: false,
    isReady: true,
    pageId: `Dashboard:Insectary:Calendar`,
    orderItemInstances: <any[]>[]
  };

  skip: number = 0;
  take?: number = undefined;
  totalCount: number = 0;
  private lastFetchedRange: string = '';

  insectaryDashboardService: InsectaryDashboardService = inject(InsectaryDashboardService);
  searchRequest: ISearchRequest = {
    columns: [],
    filters: [],
    sorts: [],
    skip: this.skip,
    take: this.take
  };
  tableFilters: ISearchRequestFilter[] = [];

  calViews = [
    { label: 'Month', value: 'dayGridMonth' },
    { label: 'Week', value: 'dayGridWeek' },
    { label: 'Day', value: 'dayGridDay' }
  ];
  selCalView = 'dayGridMonth';

  calendarOptions: CalendarOptions = {
    initialView: 'dayGridMonth',
    plugins: [dayGridPlugin, interactionPlugin],
    dayMaxEvents: 3,
    moreLinkClick: (arg) => {
      const events = arg.allSegs.map((seg) => seg.event);
      this.selectedDateEvents = events;
      const calendarEl = this.calendarContainer.nativeElement;
      if (calendarEl) {
        const viewArea = calendarEl.querySelector('.fc-view-harness-active');
        if (!viewArea) return;
        const viewRect = viewArea.getBoundingClientRect();
        this.dialogStyle = {
          position: 'fixed',
          width: '500px'
        };
        this.dialogMaxHeight = viewRect.height + 'px';
      }
      this.displayMoreDialog = true;
      return 'none';
    },
    views: { dayGridMonth: { dayMaxEventRows: 3 } },
    headerToolbar: {
      left: 'today',
      center: 'title',
      right: 'prev,next'
    },
    contentHeight: 600,
    eventMouseEnter: (info) => {
      info.el.style.cursor = 'pointer';
      this.popupRequest = this.pageVars.orderItemInstances.find((x: any) => x.id === info.event.id);
      this.op.show(info.jsEvent, info.el);
    },
    eventMouseLeave: () => {
      this.op.hide();
    },
    dayCellDidMount: (info) => {
      const cellDate = this.formatDate(info.date);
      if (this.undeliverableDates.includes(cellDate)) {
        info.el.style.backgroundColor = '#f0f0f0';
      }
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = dayNames[info.date.getUTCDay()];
      if (this.undeliverableDays.includes(dayName)) {
        info.el.style.backgroundColor = '#f0f0f0';
      }
    },
    datesSet: (dateInfo) => {
      let startDate = dateInfo.startStr.split('T')[0];
      if (dateInfo.view.type === 'dayGridDay') {
        const date = new Date(startDate);
        date.setDate(date.getDate() - 1);
        startDate = this.formatDate(date);
      }

      this.greaterthanDate = startDate;
      this.lessThandate = dateInfo.endStr.split('T')[0];
      if (this.isInitialLoad) {
        this.loadCalendarData(this.greaterthanDate, this.lessThandate);
      }

      if (dateInfo.view.type === 'dayGridMonth') {
        dateInfo.view.calendar.setOption('dayMaxEvents', 3);
      } else if (dateInfo.view.type === 'dayGridWeek') {
        dateInfo.view.calendar.setOption('dayMaxEvents', 12);
      } else if (dateInfo.view.type === 'dayGridDay') {
        dateInfo.view.calendar.setOption('dayMaxEvents', false);
      }
    },
    eventClick: (info) => {
      const orderId = info.event._def.extendedProps['OrderId'];
      if (orderId) {
        this.router.navigate([`/orders/${orderId}/insectary`]);
      } else {
        console.warn('Order ID not found in event properties');
      }
    },
    eventContent: (arg) => {
      const el = document.createElement('span');

      const iconClasses = arg.event?.extendedProps['icons'];
      iconClasses?.forEach((iconClass: string, i: number) => {
        if (iconClass) {
          const icon = document.createElement('i');
          icon.className = `fa-solid ${iconClass}`;
          icon.style.marginLeft = i === 0 ? '2px' : '0px';
          icon.style.marginRight = i === 1 && iconClasses.length === 3 ? '0px' : '7px';
          el.appendChild(icon);
        }
      });

      el.appendChild(document.createTextNode(arg.event.title));
      return { domNodes: [el] };
    }
  };

  calendarColumns: string[] = [
    'SiteName',
    'SiteCode',
    'DeliveryLocationName',
    'ItemDescription',
    'ItemDescription_C',
    'ItemDescription_S',
    'ItemDescription_CS',
    'OrderId',
    'OrderSequence',
    'ItemSequence',
    'ItemStatus',
    'ItemType',
    'InstanceId',
    'InstanceSequence',
    'InstanceStatus',
    'Insect',
    'ContainerName',
    'SubstrateName',
    'DeliveryDate',
    'Note'
  ];
  itemDescName: string = 'ItemDescription';

  ngOnInit(): void {
    this.appUser = this.appInit.appUser;
    if (this.appUser) {
      this.userSettingsJson =
        !isStringEmpty(this.appUser.userSettingsJson) || isStringEqual(this.appUser.userSettingsJson, '{}')
          ? JSON.parse(this.appUser.userSettingsJson)
          : {};

      this.itemDescName = getItemDescriptionColumn(this.userSettingsJson);
    }
    this.setCurrentDate();

    this.searchSubject$.pipe(debounceTime(100), takeUntil(this.destroy$)).subscribe(() => {
      this.performSearch();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadCalendar(filters: ISearchRequestFilter[]) {
    if (isStringEmpty(this.lessThandate)) {
      this.setCurrentDate();
    }
    this.tableFilters = filters;
    this.loadCalendarData(this.greaterthanDate, this.lessThandate);
  }

  loadCalendarData(greaterthanDate: string, lessThandate: string) {
    this.isInitialLoad = true;
    this.searchRequest.filters = [...this.tableFilters];
    this.searchRequest.sorts = [];

    this.searchRequest.filters.push(<ISearchRequestFilter>{
      fieldname: `DeliveryDate`,
      logic: `and`,
      values: [
        <ISearchFilterValue>{ operator: `greaterThan`, value: greaterthanDate },
        <ISearchFilterValue>{ operator: `lessThan`, value: lessThandate }
      ]
    });

    this.searchSubject$.next();
  }

  calViewChanged(event: any) {
    this.calendar.getApi().changeView(event.value);
  }

  private performSearch() {
    const currentRequestJson = JSON.stringify(this.searchRequest.filters);
    if (this.lastRequestJson === currentRequestJson) {
      return;
    }
    this.lastRequestJson = currentRequestJson;

    this.pageVars.isReady = false;
    this.searchRequest.columns = [...this.calendarColumns, this.itemDescName];

    firstValueFrom(this.insectaryDashboardService.getCalendar(this.searchRequest))
      .then((response) => {
        this.pageVars.orderItemInstances = JSON.parse(response?.results ?? `[]`).map((instance: any) => ({
          ...instance,
          allDay: true,
          title: this.instanceTitle(instance),
          display: this.instanceFullTitle(instance),
          date: instance.DeliveryDate,
          id: instance.InstanceId,
          backgroundColor: this.instanceColor(instance),
          borderColor: this.instanceBorderColor(instance),
          extendedProps: { icons: this.instanceIcons(instance) }
        }));

        this.calendarOptions = {
          ...this.calendarOptions,
          events: [...this.pageVars.orderItemInstances]
        };
        this.calendarReady.emit();
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: 'InsectaryCalendarComponent',
          action: 'LoadInstances:Error'
        });
      })
      .finally(() => {
        this.pageVars.isReady = true;
      });
  }

  private formatDate(dt: Date): string {
    const year = dt.getFullYear().toString();
    const month = (dt.getMonth() + 1).toString().padStart(2, '0');
    const day = dt.getDate().toString().padStart(2, '0');
    return year + '-' + month + '-' + day;
  }

  instanceIcons(instance: any): string[] {
    const icons: string[] = [];
    if (['Draft', 'Submitted'].includes(instance.InstanceStatus)) {
      icons.push('fa-pen-to-square');
    } else if (instance.InstanceStatus === 'Approved') {
      icons.push('fa-circle-check');
    } else if (instance.InstanceStatus === 'Denied') {
      icons.push('fa-circle-xmark');
    } else if (instance.InstanceStatus === 'Cancelled') {
      icons.push('fa-circle-xmark');
    } else if (instance.InstanceStatus === 'Delivered') {
      icons.push('fa-box');
    } else if (instance.InstanceStatus === 'Completed') {
      icons.push('fa-flag-checkered');
    } else if (instance.InstanceStatus == 'Delayed') {
      icons.push('fa-calendar-xmark');
    }

    if (instance.ItemType === 'Insect') {
      icons.push('fa-bug');
    } else if (instance.ItemType === 'Substrate') {
      icons.push('fa-flask');
    } else if (instance.ItemType === 'Insect + Substrate') {
      icons.push('fa-bug', 'fa-flask');
    }
    return icons;
  }

  instanceTitle(instance: any): string {
    const title = ['Insect', 'Insect + Substrate'].includes(instance.ItemType) ? instance.Insect : instance.SubstrateName;
    if (title.length > 20) {
      return title.substring(0, 20) + '...';
    } else {
      return title;
    }
  }

  instanceFullTitle(instance: any): string {
    const title = ['Insect', 'Insect + Substrate'].includes(instance.ItemType) ? instance.Insect : instance.SubstrateName;
    return title;
  }

  instanceColor(instance: any): string {
    return instance.ItemType === 'Insect'
      ? '#4271a2ff'
      : instance.ItemType === 'Substrate'
        ? 'rgba(96, 119, 40, 1)'
        : instance.ItemType === 'Insect + Substrate'
          ? '#e0a21eff'
          : '';
  }

  instanceBorderColor(instance: any): string {
    return instance.ItemType === 'Insect'
      ? '#4271a2ff'
      : instance.ItemType === 'Substrate'
        ? 'rgba(96, 119, 40, 1)'
        : instance.ItemType === 'Insect + Substrate'
          ? '#e0a21eff'
          : '';
  }

  setCurrentDate() {
    const today = new Date();
    this.greaterthanDate = this.formatDate(new Date(today.getFullYear(), today.getMonth(), 0));
    this.lessThandate = this.formatDate(new Date(today.getFullYear(), today.getMonth() + 1, 1));
  }

  getPopupData(event: any) {
    return this.pageVars.orderItemInstances.find((x: any) => x.id === event.id);
  }

  handleDialogMouseEnter(eventData: any, domEvent: MouseEvent) {
    clearTimeout(this.hoverTimeout);
    const target = domEvent.currentTarget as HTMLElement;
    if (!target) return;

    this.popupRequest = this.getPopupData(eventData);

    if (this.op?.overlayVisible) {
      this.op.hide();
    }

    this.hoverTimeout = setTimeout(() => {
      if (!document.body.contains(target)) return;
      if (this.op) {
        this.op.show(domEvent, target);
      }
    }, 150);
  }

  handleDialogMouseLeave() {
    clearTimeout(this.hoverTimeout);
    this.hoverTimeout = setTimeout(() => {
      if (this.op) {
        this.op.hide();
      }
    }, 200);
  }
}
